import { Injectable } from '@angular/core';
import { Analytics } from '@angular/fire/analytics';
import { Auth, authState, signInAnonymously } from '@angular/fire/auth';
import { collectionData, collectionSnapshots, docData, Firestore } from '@angular/fire/firestore';
import { Functions } from '@angular/fire/functions';
import { FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { httpsCallable } from '@firebase/functions';
import { Platform, ToastController } from '@ionic/angular';
import { getUnixTime } from 'date-fns';
import { addDoc, collection, deleteDoc, doc, query, QueryConstraint, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { lastValueFrom, Observable, of, switchMap, take } from 'rxjs';
import { validation_messages } from '../models/formValidationMessages';

@Injectable({
	providedIn: 'root'
})
export class FirebaseService {
	user$: Observable<any>;
	isLoggedIn = false;
	splitPaneIsLocked = false; //boolean to use split pane (side menu layout on desktop) if user is logged in
	validationMessages: any = validation_messages;
	defaultAvatar = 'assets/img/default-avatar.png';
	emailPattern = /^\S+@\S+\.\S+$/;
	emailPatternValidator = Validators.pattern('^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+.[a-zA-Z0-9-.]+$');
	phonePatternValidator = Validators.pattern(/^(\+\d{1,2}\s?)?1?\-?\.?\s?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/);

	constructor(public firestore: Firestore, public fireAuth: Auth, public fireAnalytics: Analytics, public toast: ToastController, public platform: Platform, public functions: Functions) {
		this.user$ = authState(this.fireAuth).pipe(
			switchMap((user: any) => {
				if (user && user.uid) {
					// const token = await firebase.auth().currentUser.getIdTokenResult();
					// console.log('TOKEN: ', token);
					// console.log('User Logged In: ', user);
					// Set the document ref for the user
					console.log('~~Auth User Logged in');
					const userRef = doc(firestore, `users/${user.uid}`);

					this.isLoggedIn = true;
					// Return the Observable to listen to user changes
					return docData(userRef);
				} else {
					this.isLoggedIn = false;

					// Return a null Obserable with no user
					console.log('~~No user Logged in');
					return of(null);
				}
			})
		);
	}

	getUser(): Promise<any> {
		return lastValueFrom(this.user$.pipe(take(1)));
	}

	async checkUserCreateAnonymous() {
		const user = await this.getUser();

		if (!user) {
			const uid = await this.anonymousLogin();
			return uid;
		}

		return user.id;
	}

	async anonymousLogin() {
		try {
			const auth = await signInAnonymously(this.fireAuth);
			console.log('successful login: ', auth);
			// this.trackAnalyticsEvent(AnalyticEvent.anonymous_signup, { user_id: auth.user.uid })
			await this.checkCreateUserFromAnonymousAuth(auth);

			return auth.user.uid;
		} catch (err) {
			console.log(err);
			return null;
		}
	}

	async checkCreateUserFromAnonymousAuth(auth: any) {
		console.log(`Auth: `, auth);
		if (!auth.user) {
			console.log('Auth has no user');
			return null;
		}

		console.log(`Auth User `, auth.user);
		const userDoc = await lastValueFrom(this.getFirestoreDocData(`users/${auth.user.uid}`).pipe(take(1)));

		console.log(`DOC `, userDoc);
		// Check if user is not in firestore. If so then create it.
		if (!userDoc) {
			const user: any = {
				id: auth.user.uid,
				first_name: null,
				avatar: null,
				email: null,
				phone: null,
				signed_up_anonymously: true,
				created_at: serverTimestamp()
			};

			return await this.createUser(user);
		}

		return null;
	}

	private async createUser(user: any) {
		console.log(`Creating User `, user);
		await this.setFirestoreDoc(`users/${user.id}`, user);
		// this.trackAnalyticsEvent(AnalyticEvent.sign_up);
		return { isNew: true, user };
	}

	logout() {
		return this.fireAuth.signOut();
	}

	getFirestoreDocData(ref: string) {
		const docRef = doc(this.firestore, ref);
		return docData(docRef);
	}

	getFirestoreCollection(ref: string, whereClauses: QueryConstraint[]) {
		const c = collection(this.firestore, ref);
		const q = query(c, ...whereClauses);

		return collectionData(q);
	}

	getFirestoreCollectionSnapshots(ref: string, whereClauses: QueryConstraint[]) {
		const c = collection(this.firestore, ref);
		const q = query(c, ...whereClauses);

		console.log(`CLAUSES: `, ...whereClauses);
		console.log(`QUERY: `, q);

		return collectionSnapshots(q);
	}

	addToFirestoreCollection(ref: string, obj: any) {
		const c = collection(this.firestore, ref);
		return addDoc(c, obj);
	}

	setFirestoreDoc(ref: string, obj: any) {
		const docRef = doc(this.firestore, ref);
		return setDoc(docRef, obj, { merge: true });
	}

	updateFirestoreDoc(ref: string, obj: any) {
		const docRef = doc(this.firestore, ref);
		return updateDoc(docRef, obj);
	}

	deleteFirestoreDoc(ref: string) {
		const docRef = doc(this.firestore, ref);
		return deleteDoc(docRef);
	}

	callableFunction(name: string, params: any) {
		return httpsCallable(this.functions, name)(params);
	}

	formatPhoneNumber(str: string) {
		//Filter only numbers from the input
		const cleaned = ('' + str).replace(/\D/g, '');

		//Check if the input is of correct
		const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);

		if (match) {
			//Remove the matched extension code
			//Change this to format for any country code.
			const intlCode = match[1] ? '+1 ' : '';
			return [intlCode, match[2], '-', match[3], '-', match[4]].join('');
		}

		return null;
	}

	normalizePhoneNumber(phone: string | number | null | undefined, prependPlus = false) {
		if (phone) {
			const cleaned = ('' + phone).replace(/\D/g, '');

			//Check inputs
			const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
			let internationalCode = null;

			if (match) {
				internationalCode = match[1] ? (prependPlus ? '+1' : '1') : null;

				if (!internationalCode) {
					internationalCode = prependPlus ? '+1' : '1';
				}

				return [internationalCode, match[2], match[3], match[4]].join('');
			}
		}

		return null;
	}

	getFirebaseTimestamp() {
		return serverTimestamp();
	}

	getUnixTimestamp() {
		return getUnixTime(new Date());
	}

	checkDisplayFormErrors(form: FormGroup) {
		let e = 'Please check form fields are filled out.';

		Object.keys(form.controls).forEach((key) => {
			const controlErrors: ValidationErrors | null = form.get(key)?.errors || null;
			if (controlErrors != null) {
				Object.keys(controlErrors).forEach((keyError) => {
					console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);

					if (this.validationMessages[key]) {
						const m = this.validationMessages[key].filter((k: any) => k.type === keyError)[0];

						if (m && m.message) {
							e = m.message;
						}
					}
				});
			}
		});

		this.showErrorToast(e, 'top');

		return null;
	}

	shuffle(a: any[]) {
		for (let i = a.length - 1; i > 0; i--) {
			const j = Math.floor(Math.random() * (i + 1));
			[a[i], a[j]] = [a[j], a[i]];
		}
		return a;
	}

	randomNumInRange(min = 0, max = 100) {
		// find diff
		const difference = max - min;

		// generate random number
		let rand = Math.random();

		// multiply with difference
		rand = Math.floor(rand * difference);

		// add with min value
		rand = rand + min;

		return rand;
	}

	async showToast(message: string, position: 'bottom' | 'middle' | 'top' = 'bottom') {
		const toast = await this.toast.create({
			message,
			position,
			cssClass: 'toast',
			duration: 3000
		});
		toast.present();
	}

	async showToastWithCheckmark(message: string, cssClass: string | null = null, duration = 3000) {
		const toast = await this.toast.create({
			message: `<ion-icon name="checkmark-circle"></ion-icon> ${message}`,
			position: 'bottom',
			cssClass: cssClass || 'toast',
			duration
		});
		toast.present();
	}

	async showErrorToast(error_message: string = '', position: any = 'middle') {
		const toast = await this.toast.create({
			message: error_message,
			position,
			cssClass: 'error-toast',
			duration: 3000
		});
		toast.present();
	}
}
